import {
  Controller,
  type Control,
  type FieldErrors,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { OptionSelect } from '@/components/common/OptionSelect';
import { GeoUnitSelect } from '@/components/common/GeoUnitSelect';
import { ReportingAuthoritySelect } from '@/components/common/ReportingAuthoritySelect';
import {
  getUserFieldLabel,
  isGeoUnitRequired,
  isRequiredUserField,
  type LookupUserField,
} from '../../constants/user.constants';
import { SHARE_PARENT_GRID_CLASS, type UserFormLookups } from './types';

interface SelectFieldProps<T extends FieldValues> {
  name: LookupUserField;
  control: Control<T>;
  errors: FieldErrors<T>;
  userTypeId: number;
  lookups: UserFormLookups;
  initialDivisionId?: number | null | undefined;
  initialDistrictId?: number | null | undefined;
  initialParentUserName?: string | undefined;
}

export function SelectField<T extends FieldValues>({
  name,
  control,
  errors,
  userTypeId,
  lookups,
  initialDivisionId,
  initialDistrictId,
  initialParentUserName,
}: SelectFieldProps<T>) {
  const label = getUserFieldLabel(name);
  const isGeoUnit = name === 'geoUnitId';
  const required = isGeoUnit ? isGeoUnitRequired(userTypeId) : isRequiredUserField(name);
  const error = errors[name as Path<T>];
  const errorMessage = typeof error?.message === 'string' ? error.message : undefined;

  return (
    <div className={isGeoUnit ? SHARE_PARENT_GRID_CLASS : 'flex flex-col gap-1.5'}>
      {!isGeoUnit && (
        <Label htmlFor={name} className="gap-1">
          <span>
            {label}
            {required && <span className="text-destructive">*</span>}
          </span>
        </Label>
      )}
      <Controller
        name={name as Path<T>}
        control={control}
        render={({ field }) => {
          const common = { id: name, value: field.value, onChange: field.onChange };
          switch (name) {
            case 'parentUserId':
              return (
                <ReportingAuthoritySelect
                  {...common}
                  userTypeId={userTypeId}
                  currentName={initialParentUserName}
                />
              );
            case 'companyId':
              return (
                <OptionSelect
                  {...common}
                  options={lookups.companies}
                  isLoading={lookups.companiesLoading}
                  placeholder="Select company"
                  clearable={!required}
                />
              );
            case 'departmentId':
              return (
                <OptionSelect
                  {...common}
                  options={lookups.departments}
                  isLoading={lookups.departmentsLoading}
                  placeholder="Select department"
                  clearable={!required}
                />
              );
            case 'designationId':
              return (
                <OptionSelect
                  {...common}
                  options={lookups.designations}
                  isLoading={lookups.designationsLoading}
                  clearable={!required}
                  placeholder="Select designation"
                />
              );
            case 'geoUnitId':
              return (
                <GeoUnitSelect
                  value={field.value}
                  onChange={field.onChange}
                  divisionId={initialDivisionId}
                  districtId={initialDistrictId}
                  required={required}
                  error={errorMessage}
                  className={SHARE_PARENT_GRID_CLASS}
                />
              );
          }
        }}
      />
      {!isGeoUnit && errorMessage && <p className="text-xs text-destructive">{errorMessage}</p>}
    </div>
  );
}
